import { AxiosError } from "axios";

import shipvistaAPIv2 from "./shipvistaAPIv2";
import { V2PublicTrackingResult } from "@/mappers/tracking/v2PublicTrackingMapper";

/**
 * NEXT-TRACKING-V2-CUTOVER — the v2 public tracking API surface.
 *
 * `GET /v2/tracking/{trackingNumber}?carrier=<code>` (ShipvistaAPI 08-tracking.md §4/§6/§12 T5,
 * verified vs the merged C# on origin/dev 2026-07-24):
 *   - UNAUTHENTICATED by design — no `Authorization` header, no tenant id, no body. `carrier` is a
 *     REQUIRED query param (v1 sent both values in a POST body); missing/blank → 400
 *     `carrier-required` (problem+json).
 *   - 200 → `PublicTrackingResult` (camelCase, enums as strings). `branding.isBranded` is the gate.
 *   - 404 → `tracking-not-found` (the lookup found no row for the exact carrier+trackingNumber pair).
 *   - 429 `rate-limited` is spec'd but NOT implemented (endpoint remarks: "rate-limiting is a
 *     fast-follow"). `mapTrackingError` handles a 429 defensively but it is UNVERIFIED.
 *
 * The v1 `POST Shipments/GetTrackingDetails` (200 + `status:false`) trap is gone: this client throws
 * on every non-2xx (axios default) and `mapTrackingError` normalizes the problem+json into a single
 * friendly message + the raw `code` so the page can branch (inline carrier error vs honest not-found).
 */

/**
 * RFC 7807 shape returned on any 4xx/429 from the v2 public tracking endpoint. `code` is the
 * ShipVista problem code (`carrier-required` / `tracking-not-found`) — the page branches on it.
 */
export interface TrackingProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
}

/** Known tracking error codes → user-facing copy. (More are handled by code at the call site.) */
const TRACKING_MESSAGES: Record<string, string> = {
  "carrier-required": "Please choose a carrier.",
  "tracking-not-found":
    "We couldn't find that shipment. ShipVista tracks shipments created on ShipVista — make sure the carrier and tracking number match a ShipVista label.",
};

/**
 * Normalise a thrown Axios error (v2 problem+json) into one user-facing message + the raw code + the
 * HTTP status, so the caller can branch. Mirrors `mapAuthError` / `mapGuestQuoteError`'s shape so the
 * call sites stay symmetric. This mapper is duplicated minimally here (rather than importing a
 * pre-login auth helper into a public page) — the auth mapper carries auth-only codes that would
 * couple the public tracking page to the login surface.
 *
 * Prefers a mapped known `code`, then the server `detail` / `title`, then a 429 hint (defensive —
 * the limiter is not wired), then the supplied fallback.
 */
export function mapTrackingError(
  error: unknown,
  fallback = "Something went wrong while tracking your shipment. Please try again."
): { message: string; code?: string; status?: number } {
  const ax = error as AxiosError<TrackingProblemDetails>;
  const problem = ax?.response?.data;
  const status = ax?.response?.status;

  if (problem && typeof problem === "object") {
    const code = problem.code;
    if (code && TRACKING_MESSAGES[code])
      return { message: TRACKING_MESSAGES[code], code, status };
    if (problem.detail) return { message: problem.detail, code, status };
    if (problem.title) return { message: problem.title, code, status };
  }
  // 429 is spec'd (§6) but NOT implemented by the endpoint — handled defensively, never verified.
  if (status === 429) {
    return {
      message: "Too many requests. Please try again in a minute.",
      status,
    };
  }
  return { message: fallback, status };
}

/**
 * The one public tracking call. Anonymous — no auth header is attached; `shipvistaAPIv2` is the
 * shared v2 client (no interceptor adds a Bearer). `trackingNumber` is URL-encoded into the path;
 * `carrier` is the v2 snake_case code (map UI values via `toV2CarrierCode` before calling — a wrong-
 * cased code SILENTLY 404s because the endpoint does not validate it).
 *
 * Returns the raw `V2PublicTrackingResult`; the caller maps it via `mapPublicTracking`.
 */
export const getTrackingDetails = (trackingNumber: string, carrier: string) =>
  shipvistaAPIv2.get<V2PublicTrackingResult>(
    `/tracking/${encodeURIComponent(trackingNumber)}`,
    { params: { carrier } }
  );
